import type { LabelFailureReason, LabelStatus, ProviderId, TrackingStatus } from '@trivio/contracts';
import type { FrenetDepositStatus, ProviderConnectionStatus } from '../lib/api-client';
import type { BadgeVariant } from '../components/ui/Badge';

/**
 * pt-BR labels for every closed contract enum shown in the dashboard
 * (TTP-244 notas técnicas: "a tradução pt-BR fica no cliente — nunca
 * 'inventar' status na UI"). Each map is total over its enum's members
 * (imported from `@trivio/contracts` / `api-client.ts`, never redeclared),
 * so a contract change that adds/removes a member surfaces as a TypeScript
 * error here instead of a silently-missing translation. Every lookup
 * function below still falls back to the raw value for a value that
 * somehow isn't a recognized member at runtime (should never happen, but
 * "exibir o valor bruto" beats throwing on a display path).
 */

const LABEL_STATUS_PT: Record<LabelStatus, string> = {
  processing: 'Processando',
  ready: 'Pronta',
  failed: 'Falhou',
  canceled: 'Cancelada',
};

export function labelStatusPt(status: LabelStatus | string): string {
  return LABEL_STATUS_PT[status as LabelStatus] ?? status;
}

const LABEL_STATUS_BADGE_VARIANT: Record<LabelStatus, BadgeVariant> = {
  processing: 'accent',
  ready: 'success',
  failed: 'error',
  canceled: 'neutral',
};

/** Shared by `LabelsPage.tsx` and `LabelDetailPage.tsx` so the status → badge color mapping never drifts between the list and the detail view. */
export function labelStatusBadgeVariant(status: LabelStatus | string): BadgeVariant {
  return LABEL_STATUS_BADGE_VARIANT[status as LabelStatus] ?? 'neutral';
}

const LABEL_FAILURE_REASON_PT: Record<LabelFailureReason, string> = {
  PRICE_CHANGED: 'Preço mudou além da tolerância',
  PROVIDER_REJECTED: 'Provedor rejeitou a solicitação',
  AUTH_ERROR: 'Falha de autenticação com o provedor',
  INVALID_ADDRESS: 'Endereço inválido',
  INSUFFICIENT_BALANCE: 'Saldo insuficiente no provedor',
};

export function labelFailureReasonPt(reason: LabelFailureReason | string): string {
  return LABEL_FAILURE_REASON_PT[reason as LabelFailureReason] ?? reason;
}

const TRACKING_STATUS_PT: Record<TrackingStatus, string> = {
  created: 'Criado',
  posted: 'Postado',
  in_transit: 'Em trânsito',
  out_for_delivery: 'Saiu para entrega',
  delivered: 'Entregue',
  exception: 'Ocorrência',
  returned: 'Devolvido',
};

export function trackingStatusPt(status: TrackingStatus | string): string {
  return TRACKING_STATUS_PT[status as TrackingStatus] ?? status;
}

const PROVIDER_ID_PT: Record<ProviderId, string> = {
  frenet: 'Frenet',
  superfrete: 'SuperFrete',
  melhor_envio: 'Melhor Envio',
};

export function providerIdPt(provider: ProviderId | string): string {
  return PROVIDER_ID_PT[provider as ProviderId] ?? provider;
}

const PROVIDER_CONNECTION_STATUS_PT: Record<ProviderConnectionStatus, string> = {
  active: 'Ativo',
  invalid_credentials: 'Credencial inválida',
  disconnected: 'Desconectado',
};

export function providerConnectionStatusPt(status: ProviderConnectionStatus | string): string {
  return PROVIDER_CONNECTION_STATUS_PT[status as ProviderConnectionStatus] ?? status;
}

const PROVIDER_CONNECTION_STATUS_BADGE_VARIANT: Record<ProviderConnectionStatus, BadgeVariant> = {
  active: 'success',
  invalid_credentials: 'error',
  disconnected: 'neutral',
};

/** Same status → badge color mapping `ProviderConnectCard.tsx` already renders inline — extracted here so `OverviewPage.tsx` (TTP-282) doesn't hand-roll a second copy. */
export function providerConnectionStatusBadgeVariant(status: ProviderConnectionStatus | string): BadgeVariant {
  return PROVIDER_CONNECTION_STATUS_BADGE_VARIANT[status as ProviderConnectionStatus] ?? 'neutral';
}

/** TTP-270 — Frenet wallet deposit status (`orgs/{orgId}/frenet_deposits/{depositId}.status`). */
const FRENET_DEPOSIT_STATUS_PT: Record<FrenetDepositStatus, string> = {
  pending: 'Pendente',
  succeeded: 'Concluído',
  failed: 'Falhou',
};

export function frenetDepositStatusPt(status: FrenetDepositStatus | string): string {
  return FRENET_DEPOSIT_STATUS_PT[status as FrenetDepositStatus] ?? status;
}

const FRENET_DEPOSIT_STATUS_BADGE_VARIANT: Record<FrenetDepositStatus, BadgeVariant> = {
  pending: 'accent',
  succeeded: 'success',
  failed: 'error',
};

export function frenetDepositStatusBadgeVariant(status: FrenetDepositStatus | string): BadgeVariant {
  return FRENET_DEPOSIT_STATUS_BADGE_VARIANT[status as FrenetDepositStatus] ?? 'neutral';
}
